import React, { useState } from "react";
import { Check } from "lucide-react";
import { Input } from "./ui/input";

export const GOLD = "bg-[linear-gradient(135deg,#ffe08a_0%,#f5b400_45%,#d99700_100%)]";

export const GLASS_INPUT =
  "h-12 rounded-xl bg-[#0e1218] border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#f5b400] focus-visible:shadow-[0_0_0_4px_rgba(245,180,0,0.15),0_0_24px_-6px_rgba(245,180,0,0.6)] transition-[box-shadow,border-color]";

export function GlassCard({ children, testid }) {
  return (
    <div className="rounded-3xl p-[1px] bg-[linear-gradient(160deg,rgba(245,180,0,0.55),rgba(245,180,0,0.08)_35%,rgba(255,255,255,0.06)_70%,rgba(245,180,0,0.35))]" data-testid={testid}>
      <div className="relative rounded-3xl bg-[#0b0e13]/95 backdrop-blur-2xl overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-[#f5b400]/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-28 -left-16 h-56 w-56 rounded-full bg-emerald-500/10 blur-3xl" />
        {children}
      </div>
    </div>
  );
}

export function CardHeader({ title, sub, icon: Icon, badge = "INR" }) {
  return (
    <div className="relative flex items-center justify-between px-4 pt-4">
      <div>
        <h3 className="font-display font-extrabold text-xl text-white leading-none">{title}</h3>
        <p className="mt-1 text-[11px] text-slate-400 inline-flex items-center gap-1">{Icon && <Icon className="h-3 w-3 text-emerald-400" />} {sub}</p>
      </div>
      <span className="rounded-full bg-white/5 ring-1 ring-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-300">{badge}</span>
    </div>
  );
}

export function StepRail({ steps, current, onStep }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-4" data-testid="step-rail">
      {steps.map((s, i) => {
        const active = current === s.key;
        const done = current > s.key;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`h-px flex-1 ${done || active ? "bg-[#f5b400]/60" : "bg-white/10"}`} />}
            <button type="button" onClick={() => s.enabled !== false && onStep(s.key)} data-testid={s.testid}
              className={`inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                active ? "text-[#f5b400]" : done ? "text-emerald-300" : "text-slate-500"}`}>
              <span className={`grid place-items-center h-6 w-6 rounded-full text-[11px] font-extrabold ${
                active ? `${GOLD} text-black shadow-[0_0_14px_rgba(245,180,0,0.55)]` : done ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40" : "bg-white/5 text-slate-400 ring-1 ring-white/10"}`}>
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              {s.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function Cta({ onClick, disabled, children, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
      className={`group relative w-full h-14 rounded-2xl overflow-hidden font-display font-extrabold text-[15px] uppercase tracking-[0.08em] text-black transition-[transform,box-shadow,filter] duration-300 ${GOLD}
        enabled:hover:-translate-y-0.5 enabled:hover:shadow-[0_18px_40px_-14px_rgba(245,180,0,0.85)] enabled:hover:brightness-105 enabled:active:scale-[0.98]
        disabled:opacity-40 disabled:saturate-50 disabled:cursor-not-allowed shadow-[0_12px_30px_-14px_rgba(245,180,0,0.7)]`}>
      <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-white/40 blur-md -skew-x-12 transition-transform duration-700 group-enabled:group-hover:translate-x-[400%]" />
      <span className="relative inline-flex items-center justify-center gap-2">{children}</span>
    </button>
  );
}

export function AmountChips({ chips, value, onPick, prefix = "wd-quick", tag }) {
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {chips.map((c) => {
        const sel = value === c;
        return (
          <button key={c} type="button" onClick={() => onPick(c)} data-testid={`${prefix}-${c}`}
            className={`relative h-14 rounded-2xl font-display font-bold text-[15px] tabular-nums transition-[transform,background-color,box-shadow,color] duration-200 active:scale-95 ${
              sel ? `${GOLD} text-black shadow-[0_10px_24px_-10px_rgba(245,180,0,0.8)] scale-[1.02]`
                  : "bg-white/[0.04] text-white ring-1 ring-white/10 hover:ring-[#f5b400]/50 hover:bg-white/[0.07]"}`}>
            <span className={`text-[11px] font-semibold mr-0.5 ${sel ? "text-black/70" : "text-slate-400"}`}>₹</span>{c.toLocaleString("en-IN")}
            {tag && <span className={`absolute -top-1.5 right-2 rounded-full px-1.5 py-px text-[9px] font-extrabold ${sel ? "bg-black text-[#f5b400]" : "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30"}`}>{tag}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function AmountField({ value, onChange, min, max, valid, invalid, testid }) {
  const [focused, setFocused] = useState(false);
  const n = Number(value) || 0;
  return (
    <div className={`relative flex items-center rounded-2xl px-4 h-16 transition-[box-shadow,border-color] duration-300 bg-[#0e1218] border ${
      focused ? "border-[#f5b400] shadow-[0_0_0_4px_rgba(245,180,0,0.15),0_0_28px_-6px_rgba(245,180,0,0.6)]"
      : invalid ? "border-rose-500/60" : valid ? "border-emerald-400/50" : "border-white/10"}`}>
      <span className={`font-display text-2xl font-bold mr-2 ${focused || n ? "text-[#f5b400]" : "text-slate-500"}`}>₹</span>
      <Input type="number" min={min} max={max} value={value} onChange={onChange} placeholder="Enter amount"
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} inputMode="numeric"
        className="border-0 bg-transparent px-0 h-full text-white font-display font-bold text-2xl placeholder:text-slate-600 placeholder:font-semibold placeholder:text-lg focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        data-testid={testid} />
      {valid && <Check className="h-5 w-5 text-emerald-400" />}
    </div>
  );
}
